import React, { useState, useEffect } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import { MemberDTO, BatchFileLogDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useBatchFileLogApi } from '../../hooks/useBatchFileLogApi';
import BatchFileLogTable from '../tables/BatchFileLogTable';

interface MemberFormProps {
  initialValues: MemberDTO;
  onSubmit: (data: MemberDTO) => void;
  onCancel: () => void;
}

const MemberForm: React.FC<MemberFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<MemberDTO>(initialValues);
  
  // Add states for batch file logs
  const [batchFileLogs, setBatchFileLogs] = useState<BatchFileLogDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const { search: searchBatchFileLogs } = useBatchFileLogApi();

  // Function to fetch batch file logs
  const fetchBatchFileLogs = async () => {
    if (!formData.id) return;

    try {
      setIsLoadingLogs(true);
      const response = await searchBatchFileLogs({
        filters: {
          memberId: formData.id
        },
        page: currentPage,
        pageSize: pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true
      });

      if (response?.data) {
        setBatchFileLogs(response.data.results || []);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
      }
    } catch (error) {
      console.error('Error fetching batch file logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Fetch logs when component mounts or when pagination changes
  useEffect(() => {
    fetchBatchFileLogs();
  }, [formData.id, currentPage, pageSize]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData); // Pass the updated data back to the parent
  };

  return (
    <div>
      <form onSubmit={handleFormSubmit}>
        <div className="mb-4">
          <FormField
            id="membershipNumber"
            label="Membership Number"
            type="text"
            value={formData.membershipNumber || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Membership Number"
          />
          <FormField
            id="firstName"
            label="First Name"
            type="text"
            value={formData.firstName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter First Name"
          />
           <FormField
            id="lastName"
            label="Last Name"
            type="text"
            value={formData.lastName || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Last Name"
          />
          <FormField
            id="email"
            label="Email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Email"
          />
          <FormField
            id="phone"
            label="Phone"
            type="text"
            value={formData.phone || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Phone"
          />
        </div>
        <div className="flex justify-end space-x-4">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>

      {formData.id && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Debit Order Batch Files Requests</h3>
          <BatchFileLogTable
            data={batchFileLogs}
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoadingLogs}
          />
        </div>
      )}
    </div>
  );
};

export default MemberForm;
